import api from 'api';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { LTHeader, LTItem, LTRow, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import { CustomLTRow } from '../../../../../customLTRow/customLTRow';
import Skeleton from '@mui/material/Skeleton';
import Checkbox from 'components/common/Checkbox/Checkbox';
import CheckboxWithLabel from 'components/common/CheckboxWithLabel/CheckboxWithLabel';
import { PaymentStatus } from 'api/models/payload/ClientsPayload';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import type { VisaDetails } from 'api/rest/application';
import { SalesClient } from 'api';
import { loadApp } from 'redux/application/application.middleware';
import { setActiveApp } from 'redux/application/application.slice';
import { setAlert } from 'redux/alert';
import { shouldCollectVisaDetails } from 'helpers/program';
import { useAuth } from 'contexts/ProtectedRoute';
import ROLE from '../../../../../../../../assets/userRoles.json';
import * as S from '../../../../../Table.styled';
import { useTranslation } from 'react-i18next';
import ColumnTextFilter from 'components/common/columnTextFilter';
import { CasesIdTypeToggle } from './casesIdTypeToggle';
import { PaymentStatusMenuButton } from './paymentStatusMenuButton';
import ToggleableDropdown from 'components/common/ToggleableDropdown/ToggleableDropdown';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { programNamesForCountries } from 'utils/transformProgramsToResults';
import AgentDropdown from 'components/common/AgentDropdown/AgentDropdown';
import { AgentRoles } from 'api/models/payload/AgentRoles';
import { Agent } from 'types';
import StatusDropdown from 'components/common/StatusDropdown/StatusDropdown';
import { caseStatusOptions, StatusColor } from 'data/constants/caseStatus';
import ResultsAutcomeModal from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/shared/ResultsOutcomeModal/ResOutcomeModal';
import ReceivedVisaModal from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/shared/ReceivedVisaModal/ReceivedVisaModal';
import SwapVert from '@mui/icons-material/SwapVert';
import { IconButton } from '@mui/material';
import { ReactComponent as ExclMark } from 'assets/icons/B2B/round_exclamation_mark.svg';
import { getDaysSince } from 'utils/dateUtils';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DashboardFilter } from '../types';
import {
    caseIdDisplayValue,
    casesIdTypeLocaleKey,
    readStoredCasesIdType,
    writeStoredCasesIdType,
    type CasesIdType,
} from 'helpers/dashboardCases';
import useTranslate from 'hooks/useTranslate';
import OptionDropdown, { DropdownOption } from 'components/common/OptionDropdown/OptionDropdown';

export const iconEligibleStatuses = new Set([
    'IN_REVIEW',
    'READY_FOR_SUBMISSION',
    'AWAITING_ITA',
    'SUBMITTED',
    'VISA_RESULTS',
    'AWAITING_LOA',
]);

type OptionType = {
    value: string;
    label: string;
};
type StatusOption = {
    label: string;
    color: string;
    value?: string;
};
type ClientsTableProps = {
    clientsApps: SalesClient[];
    clientsAppsForTable: SalesClient[];
    setClientsAppsForTable: (apps: SalesClient[]) => void;
    isClientsAppsLoading: boolean;
    reloadClients: () => void;
    mobile: boolean;
    onFilterChange: (filters: DashboardFilter) => void;
    organizations: DropdownOption[];
};

const CasesApplicationsTable = ({
    clientsApps,
    clientsAppsForTable,
    setClientsAppsForTable,
    isClientsAppsLoading,
    reloadClients,
    mobile,
    onFilterChange,
    organizations,
}: ClientsTableProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { names: enabledCountryNames } = useEnabledCountries();
    const { t } = useTranslation();
    const { translate } = useTranslate();
    const dispatch = useDispatch<AppDispatch>();
    const tableRef = useRef<HTMLDivElement | null>(null);
    const [selectedApps, setSelectedApps] = useState<number[]>([]);
    const [checkedHeader, setCheckedHeader] = useState<boolean>(false);
    const [salesAgents, setSalesAgents] = useState<null | Agent[]>(null);
    const [backofficeAgents, setBackofficeAgents] = useState<null | Agent[]>(null);
    const [visaAgents, setVisaAgents] = useState<null | Agent[]>(null);

    const skeletonArray = clientsAppsForTable.length > 0 ? clientsAppsForTable : [1, 2, 3];
    const { role } = useAuth();
    const canSetPaymentStatus = [
        ROLE.sales,
        ROLE.backofficeAdmin,
        ROLE.superAdmin,
        ROLE.superSuperAdmin,
    ].includes(role);
    const canAssignClients =
        role !== ROLE.sales && role !== ROLE.consultant && role !== ROLE.backofficeConsultant;

    useEffect(() => {
        localStorage.setItem('selectedApps', JSON.stringify(selectedApps));
    }, [selectedApps]);

    useEffect(() => {
        //this is needed to reset the select app checkboxes after assigning clients
        setSelectedApps([]);
        setCheckedHeader(false);
    }, [clientsApps]);

    useEffect(() => {
        setClientsAppsForTable(clientsApps);
    }, [clientsApps, setClientsAppsForTable]);

    const [outcomeAppId, setOutcomedAppId] = useState<string>();
    const [showResultsOutcomeModal, setShowResultsOutcomeModal] = useState(false);
    const [showReceivedVisaModal, setShowReceivedVisaModal] = useState(false);
    const [pendingVisaApplication, setPendingVisaApplication] = useState<SalesClient | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | ClientApplicationStatus>('');
    const handleConfirmedResOutcome = async (value: 'success' | 'fail') => {
        if (
            outcomeAppId &&
            selectedStatus &&
            selectedStatus !== t('selectStatus', { lng: language })
        ) {
            const collectVisaDetails = shouldCollectVisaDetails(
                pendingVisaApplication?.program_name
            );

            if (value === 'success' && collectVisaDetails) {
                setShowResultsOutcomeModal(false);
                setShowReceivedVisaModal(true);
                return;
            }

            try {
                const resp = await api.application.patch(outcomeAppId, { status: value });
                if (resp?.status === 200 || resp?.status === 201) {
                    await saveNewStatus(outcomeAppId, selectedStatus, {
                        visa_result: value === 'success' ? 'POSITIVE' : 'NEGATIVE',
                    });
                }
                setOutcomedAppId(undefined);
                setPendingVisaApplication(null);
                setSelectedStatus('');
            } catch (err) {
                console.log(err);
                setShowResultsOutcomeModal(false);
            }
        }
    };

    const saveResultsOutcome = (application: SalesClient, selected: string) => {
        if (selected === 'VISA_RESULTS') {
            setOutcomedAppId(application.id.toString());
            setPendingVisaApplication(application);
            setShowResultsOutcomeModal(true);
        } else {
            void saveNewStatus(application.id.toString(), selected).catch(console.error);
        }
    };
    const saveNewStatus = async (
        appId: string,
        selected: string | ClientApplicationStatus,
        visaResult?: {
            visa_result: 'POSITIVE' | 'NEGATIVE';
            visa?: VisaDetails;
        }
    ) => {
        const payload = {
            client_status: selected,
            ...visaResult,
        };

        await api.application.setClientApplicationStatus(appId, payload);
        reloadClients();
        setShowResultsOutcomeModal(false);
    };

    const handleVisaReceived = async (visa: VisaDetails) => {
        if (!outcomeAppId || !selectedStatus) {
            return;
        }

        const response = await api.application.patch(outcomeAppId, { status: 'success' });
        if (response?.status !== 200 && response?.status !== 201) {
            throw new Error('Unable to update the application result.');
        }

        await saveNewStatus(outcomeAppId, selectedStatus, {
            visa_result: 'POSITIVE',
            visa,
        });
        setOutcomedAppId(undefined);
        setPendingVisaApplication(null);
        setSelectedStatus('');
        setShowReceivedVisaModal(false);
    };

    const getFileStatus = (status: ClientApplicationStatus) => {
        let currentStatus = '';
        let color = '';
        if (status === ClientApplicationStatus.ASSESSMENT) {
            color = StatusColor.brown;
            currentStatus = t('assessment', { lng: language });
        } else if (status === ClientApplicationStatus.NOT_ASSIGNED) {
            color = StatusColor.gray;
            currentStatus = t('notAssigned', { lng: language });
        } else if (status === ClientApplicationStatus.CLIENT_WITH_ISSUES) {
            color = StatusColor.red;
            currentStatus = t('clientWithIssues', { lng: language });
        } else if (status === ClientApplicationStatus.PROCESSING) {
            color = StatusColor.blue;
            currentStatus = t('processing', { lng: language });
        } else if (status === ClientApplicationStatus.IN_REVIEW) {
            color = StatusColor.orange;
            currentStatus = t('inReview', { lng: language });
        } else if (status === ClientApplicationStatus.READY_FOR_SUBMISSION) {
            color = StatusColor.purple;
            currentStatus = t('readyForSubmission', { lng: language });
        } else if (status === ClientApplicationStatus.AWAITING_ITA) {
            color = StatusColor.purple;
            currentStatus = t('awaitingITA', { lng: language });
        } else if (status === ClientApplicationStatus.SUBMITTED) {
            color = StatusColor.green;
            currentStatus = t('submitted', { lng: language });
        } else if (status === ClientApplicationStatus.VISA_RESULTS) {
            color = StatusColor.green;
            currentStatus = t('visaResults', { lng: language });
        } else if (status === ClientApplicationStatus.AWAITING_LOA) {
            color = StatusColor.orange;
            currentStatus = t('awaitingLOA', { lng: language });
        } else if (status === ClientApplicationStatus.CLOSED) {
            color = StatusColor.black;
            currentStatus = t('closed', { lng: language });
        } else if (status === ClientApplicationStatus.ON_HOLD) {
            color = StatusColor.azure;
            currentStatus = t('onHold', { lng: language });
        } else if (status === ClientApplicationStatus.REMOVED) {
            color = StatusColor.red;
            currentStatus = t('removed', { lng: language });
        } else {
            color = StatusColor.red;
            currentStatus = status ? status : 'N/A';
        }
        return <S.Status color={color}>{currentStatus}</S.Status>;
    };

    const handlePaymentStatusChange = async (id: number, status: PaymentStatus) => {
        try {
            await api.setApplicationPaymentStatus.get(id.toString(), status);
            dispatch(
                setAlert({
                    message: t('paymentStatusUpdated', { lng: language }),
                    type: 'success',
                })
            );
            reloadClients();
        } catch {
            dispatch(
                setAlert({
                    message: t('somethingWentWrong', { lng: language }),
                    type: 'error',
                })
            );
        }
    };

    const handleSelectApp = async (appID: number, checked: boolean) => {
        if (checked) {
            setSelectedApps((prevApps) => [...prevApps, appID]);
        } else {
            setSelectedApps((prevApps) => prevApps.filter((id) => id !== appID));
        }
    };

    const handleChangeHeader = (checkedStatus: boolean) => {
        setCheckedHeader(checkedStatus);

        if (checkedStatus) {
            const listOfAllCases = clientsAppsForTable.map((clientCase) => clientCase.id);
            setSelectedApps(listOfAllCases);
        } else {
            setSelectedApps([]);
        }
    };

    function formatCurrency(amount: number | null) {
        if (amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        } else {
            return '$0';
        }
    }

    const formatDate = (isoDateString: string) => {
        const date = new Date(isoDateString);

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        const formattedDate = `${day}/${month}/${year}`;
        return formattedDate;
    };

    const [selectedCaseProgram, setSelectedCaseProgram] = useState<string>('');
    const [countryBasedProgramList, setCountryBasedProgramList] = useState<OptionType[]>([]);
    const [selectedFileStatus, setSelectedFileStatus] = useState<string>('');
    const [selectedCaseId, setSelectedCaseId] = useState<string>('');
    const [selectedCaseIdType, setSelectedCaseIdType] =
        useState<CasesIdType>(readStoredCasesIdType);
    const caseIdLabel = translate(casesIdTypeLocaleKey(selectedCaseIdType));
    const handleCaseIdTypeChange = (nextType: CasesIdType) => {
        setSelectedCaseIdType(nextType);
        setSelectedCaseId('');
        writeStoredCasesIdType(nextType);
    };
    const [selectedClientName] = useState<string>('');
    const [selectedClientEmail, setSelectedClientEmail] = useState<string>('');
    const [statusUpdated, setStatusUpdated] = useState<string>('');
    const [selectedPaidClients, setSelectedPaidClients] = useState<string>('');
    const [selectedBOAgent, setSelectedBOAgent] = useState<string>('');
    const [selectedVAgent, setSelectedVAgent] = useState<string>('');
    const [selectedSAgent, setSelectedSAgent] = useState<string>('');
    const [selectedOrganization, setSelectedOrganization] = useState<string>('');

    useEffect(() => {
        onFilterChange({
            program_name: selectedCaseProgram || null,
            client_status: selectedFileStatus || null,
            caseId: selectedCaseId || null,
            caseIdType: selectedCaseIdType,
            clientName: selectedClientName || null,
            clientEmail: selectedClientEmail || null,
            sort_status_updated_at: statusUpdated || null,
            paid_clients: selectedPaidClients || null,
            bo_agent: selectedBOAgent || null,
            v_agent: selectedVAgent || null,
            s_agent: selectedSAgent || null,
            service_provider_id: selectedOrganization || null,
        });
    }, [
        selectedCaseProgram,
        selectedFileStatus,
        selectedCaseId,
        selectedCaseIdType,
        selectedClientName,
        selectedClientEmail,
        statusUpdated,
        selectedPaidClients,
        selectedBOAgent,
        selectedVAgent,
        selectedSAgent,
        selectedOrganization,
        onFilterChange,
    ]);

    //works with multi domains
    const fetchProgramList = useCallback(async () => {
        try {
            const response = await api.expertiseList.get();
            const programArray = programNamesForCountries(response.data, enabledCountryNames);
            setCountryBasedProgramList(programArray.map((e) => ({ value: e, label: e })));
        } catch (e) {
            console.error(e);
        }
    }, [enabledCountryNames]);

    const convertStatusName = (statusName: ClientApplicationStatus | string) => {
        if (!statusName || statusName === t('selectStatus', { lng: language })) {
            return t('selectStatus', { lng: language });
        }

        const tempStatusName = statusName.toLowerCase().split('_').join(' ');
        const capitalizedStatusName =
            tempStatusName.charAt(0).toUpperCase() + tempStatusName.slice(1);
        return capitalizedStatusName;
    };

    const StatusesList = Object.values(ClientApplicationStatus).map(
        (status: ClientApplicationStatus) => ({
            value: status,
            label: convertStatusName(status),
        })
    );

    const isAdmin = role === ROLE.admin;

    useEffect(() => {
        dispatch(setActiveApp(null));

        let isCancelled = false;

        const fetchAgents = async () => {
            if (!isAdmin) {
                try {
                    const response = await api.agents.get(AgentRoles.SALES);
                    if (!isCancelled) setSalesAgents(response.data);
                } catch (e) {
                    console.error(e);
                }

                try {
                    const response = await api.agents.get(AgentRoles.BACKOFFICE);
                    if (!isCancelled) setBackofficeAgents(response.data);
                } catch (e) {
                    console.error(e);
                }
            }

            try {
                const response = await api.agents.get(AgentRoles.CONSULTANT);
                if (!isCancelled) setVisaAgents(response.data);
            } catch (e) {
                console.error(e);
            }
        };

        fetchAgents();

        return () => {
            isCancelled = true;
        };
        // role-derived flags are stable for the lifetime of this mounted
        // component; intentionally run only on mount to avoid re-firing the
        // three agents requests on every render (which caused a
        // "Maximum update depth exceeded" cascade through the axios
        // request interceptor's redux dispatches).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateOrganizationOnRows = useCallback(
        (applicationId: number, serviceProviderId: number | null) => {
            setClientsAppsForTable(
                clientsAppsForTable.map((clientApp) =>
                    clientApp.id === applicationId
                        ? {
                              ...clientApp,
                              service_provider_id: serviceProviderId,
                          }
                        : clientApp
                )
            );
        },
        [clientsAppsForTable, setClientsAppsForTable]
    );

    useEffect(() => {
        fetchProgramList();
    }, [fetchProgramList]);

    const saveOrganization = async (applicationId: number, organizationId: string) => {
        try {
            const nextOrganizationId = Number(organizationId);
            await api.application.patch(applicationId.toString(), {
                service_provider_id: nextOrganizationId,
            });
            updateOrganizationOnRows(applicationId, nextOrganizationId);
            reloadClients();
        } catch (error) {
            console.error(error);
        }
    };

    const assignAgents = async (
        salesAgentID: number | undefined,
        backofficeAgentID: number | undefined,
        visaAgentID: number | undefined,
        appID: number
    ) => {
        if (salesAgentID) {
            try {
                const payload = {
                    agent_id: salesAgentID,
                    application_id: appID,
                };
                await api.agents.assignAgent(AgentRoles.SALES, payload);
            } catch (e) {
                console.error(e);
            }
        }
        if (backofficeAgentID) {
            try {
                const payload = {
                    agent_id: backofficeAgentID,
                    application_id: appID,
                };
                await api.agents.assignAgent(AgentRoles.BACKOFFICE, payload);
            } catch (e) {
                console.error(e);
            }
        }
        if (visaAgentID) {
            try {
                const payload = {
                    agent_id: visaAgentID,
                    application_id: appID,
                };
                await api.agents.assignAgent(AgentRoles.CONSULTANT, payload);
            } catch (e) {
                console.error(e);
            }
        }
    };

    const toggleSortingStatus = () => {
        setStatusUpdated((prev) => (prev === '' ? 'asc' : prev === 'asc' ? 'desc' : ''));
    };
    const selectionTitle = (
        <>
            {canAssignClients && (
                <LTItem
                    tip={t('selectAll', { lng: language })}
                    maxWidth="20px"
                    minWidth="20px"
                    style={{
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        marginRight: '1rem',
                        lineHeight: '1.2',
                    }}
                >
                    <Checkbox
                        onChange={(ev) => {
                            handleChangeHeader((ev.target as HTMLInputElement).checked);
                        }}
                        checked={checkedHeader}
                    />
                </LTItem>
            )}
            {canSetPaymentStatus && (
                <LTItem
                    maxWidth="60px"
                    minWidth="60px"
                    style={{
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        marginRight: '0.4rem',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {t('paid', { lng: language })}
                    <ToggleableDropdown
                        selectedType={selectedPaidClients}
                        handleSelectedType={(value) => setSelectedPaidClients(value)}
                        dropdownMenuPrograms={[
                            {
                                label: t('all', { lng: language }),
                                value: t('all', { lng: language }),
                            },
                            {
                                value: PaymentStatus.NOT_PAID,
                                label: t('paymentStatusNotPaid', { lng: language }),
                            },
                            {
                                value: PaymentStatus.PARTIALLY_PAID,
                                label: t('paymentStatusPartiallyPaid', { lng: language }),
                            },
                            {
                                value: PaymentStatus.FULLY_PAID,
                                label: t('paymentStatusFullyPaid', { lng: language }),
                            },
                        ]}
                        purple={true}
                        label="selectCase"
                    />
                </LTItem>
            )}
        </>
    );
    const desktopTableMinWidth = '2551px';
    const casesGridStyle: React.CSSProperties = useMemo(
        () => ({
            display: 'grid',
            gridTemplateColumns: [
                canAssignClients ? '20px' : null,
                canSetPaymentStatus ? '60px' : null,
                '150px',
                '130px',
                '200px',
                '150px',
                '170px',
                '90px',
                '120px',
                '140px',
                '130px',
                '130px',
                '130px',
                '160px',
                '110px',
                '110px',
                '110px',
                '70px',
            ]
                .filter(Boolean)
                .join(' '),
            alignItems: 'center',
            columnGap: '1rem',
            width: desktopTableMinWidth,
        }),
        [canAssignClients, canSetPaymentStatus]
    );

    return (
        <>
            <S.ListTable
                ref={tableRef}
                style={
                    mobile
                        ? { maxHeight: 'unset', width: '100%', maxWidth: '1552px' }
                        : { maxHeight: 'unset', width: '97vw', marginTop: '36px' }
                }
            >
                <LTHeader
                    style={{ paddingLeft: '16px', minWidth: desktopTableMinWidth }}
                    innerStyle={mobile ? undefined : casesGridStyle}
                >
                    {/* tooltips are present in LTItem itself */}
                    {selectionTitle}
                    <LTItem
                        tip={caseIdLabel}
                        maxWidth="150px"
                        minWidth="150px"
                        style={{
                            marginRight: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <CasesIdTypeToggle
                            value={selectedCaseIdType}
                            onChange={handleCaseIdTypeChange}
                        />
                        <ColumnTextFilter
                            value={selectedCaseId}
                            onChange={setSelectedCaseId}
                            placeholder={caseIdLabel}
                        />
                    </LTItem>
                    <LTItem maxWidth="130px" minWidth="130px" style={{ marginRight: '1rem' }}>
                        {t('name', { lng: language })}
                        {/* <ToggleableDropdown
                            selectedType={selectedClientName}
                            handleSelectedType={(value) => setSelectedClientName(value)}
                            dropdownMenuPrograms={[
                                { label: t('all', { lng: language }), value: t('all', { lng: language })},
                                ...StatusesList,
                            ]}
                            purple={true}
                            label="selectName"
                        /> */}
                    </LTItem>
                    <LTItem
                        maxWidth="200px"
                        minWidth="200px"
                        style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}
                    >
                        {t('email', { lng: language })}
                        <ColumnTextFilter
                            value={selectedClientEmail}
                            onChange={setSelectedClientEmail}
                            placeholder={t('email', { lng: language })}
                        />
                    </LTItem>
                    <LTItem
                        maxWidth="150px"
                        minWidth="150px"
                        style={{
                            marginRight: '1rem',
                            display: 'flex',

                            alignItems: 'center',
                        }}
                    >
                        {t('service', { lng: language })}
                        <ColumnTextFilter
                            value={selectedCaseProgram}
                            onChange={setSelectedCaseProgram}
                            placeholder={t('service', { lng: language })}
                            options={countryBasedProgramList}
                        />
                    </LTItem>
                    <LTItem
                        maxWidth="170px"
                        minWidth="170px"
                        style={{
                            textAlign: 'center',
                            marginRight: '1rem',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {t('fileStatus', { lng: language })}
                        <ColumnTextFilter
                            value={selectedFileStatus}
                            onChange={setSelectedFileStatus}
                            placeholder={t('fileStatus', { lng: language })}
                            options={StatusesList}
                        />
                    </LTItem>
                    <LTItem
                        maxWidth="90px"
                        minWidth="90px"
                        style={{
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            lineHeight: '1.2',
                        }}
                    >
                        Tickets Open
                    </LTItem>
                    <LTItem
                        maxWidth="120px"
                        minWidth="120px"
                        style={{
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            lineHeight: '1.2',
                        }}
                    >
                        Tickets Resolved
                    </LTItem>
                    <LTItem
                        maxWidth="140px"
                        minWidth="140px"
                        style={{
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {t('daysInStatus', { lng: language })}
                        <IconButton
                            onClick={toggleSortingStatus}
                            style={{
                                cursor: 'pointer',
                                touchAction: '#dddddd',
                            }}
                        >
                            {statusUpdated === 'asc' ? (
                                <ArrowUpwardIcon />
                            ) : statusUpdated === 'desc' ? (
                                <ArrowDownwardIcon />
                            ) : (
                                <SwapVert />
                            )}
                        </IconButton>
                    </LTItem>
                    <LTItem
                        maxWidth="130px"
                        minWidth="130px"
                        style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}
                    >
                        {t('backOffice', { lng: language })}
                        <ToggleableDropdown
                            selectedType={selectedBOAgent}
                            handleSelectedType={(value) => setSelectedBOAgent(value)}
                            dropdownMenuPrograms={[
                                {
                                    label: t('all', { lng: language }),
                                    value: t('all', { lng: language }),
                                },
                                ...(backofficeAgents ?? []).map((agent) => ({
                                    label: agent.firstname + ' ' + agent.lastname,
                                    value: agent.id.toString(),
                                })),
                            ]}
                            purple={true}
                            label="selectCase"
                        />
                    </LTItem>
                    <LTItem
                        maxWidth="130px"
                        minWidth="130px"
                        style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}
                    >
                        {t('visaAgent', { lng: language })}
                        <ToggleableDropdown
                            selectedType={selectedVAgent}
                            handleSelectedType={(value) => setSelectedVAgent(value)}
                            dropdownMenuPrograms={[
                                {
                                    label: t('all', { lng: language }),
                                    value: t('all', { lng: language }),
                                },
                                ...(visaAgents ?? []).map((vAgent) => ({
                                    value: vAgent.id.toString(),
                                    label: vAgent.firstname + ' ' + vAgent.lastname,
                                })),
                            ]}
                            purple={true}
                            label="selectCase"
                        />
                    </LTItem>
                    <LTItem
                        maxWidth="130px"
                        minWidth="130px"
                        style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}
                    >
                        {t('salesAgent', { lng: language })}
                        <ToggleableDropdown
                            selectedType={selectedSAgent}
                            handleSelectedType={(value) => setSelectedSAgent(value)}
                            dropdownMenuPrograms={[
                                {
                                    label: t('all', { lng: language }),
                                    value: t('all', { lng: language }),
                                },
                                ...(salesAgents ?? []).map((sAgent) => ({
                                    value: sAgent.id.toString(),
                                    label: sAgent.firstname + ' ' + sAgent.lastname,
                                })),
                            ]}
                            purple={true}
                            label="selectCase"
                        />
                    </LTItem>
                    <LTItem
                        maxWidth="160px"
                        minWidth="160px"
                        style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}
                    >
                        {t('organization', { lng: language })}
                        <ToggleableDropdown
                            selectedType={selectedOrganization}
                            handleSelectedType={(value) => setSelectedOrganization(value)}
                            dropdownMenuPrograms={[
                                {
                                    label: t('all', { lng: language }),
                                    value: t('all', { lng: language }),
                                },
                                ...organizations,
                            ]}
                            purple={true}
                            label="selectCase"
                        />
                    </LTItem>
                    <LTItem
                        tip="Retainer Agreement Signed On"
                        maxWidth="110px"
                        minWidth="110px"
                        style={{ textAlign: 'center', marginRight: '1rem' }}
                    >
                        {t('retainer', { lng: language })}
                    </LTItem>
                    <LTItem
                        tip="Offer Sent On"
                        maxWidth="110px"
                        minWidth="110px"
                        style={{ textAlign: 'center', marginRight: '1rem' }}
                    >
                        {t('offerSent', { lng: language })}
                    </LTItem>

                    <LTItem
                        tip="Full Payment On"
                        maxWidth="110px"
                        minWidth="110px"
                        style={{ textAlign: 'center' }}
                    >
                        {t('payment', { lng: language })}
                    </LTItem>
                    <LTItem
                        maxWidth="70px"
                        minWidth="70px"
                        style={{ textAlign: 'center', marginRight: '1rem' }}
                    >
                        {t('amount', { lng: language })}
                    </LTItem>
                </LTHeader>

                {!isClientsAppsLoading && clientsAppsForTable.length === 0 && (
                    <LTPlaceholder>{t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                )}

                {!isClientsAppsLoading && clientsAppsForTable.length > 0 && (
                    <>
                        <S.SpanForMobile>
                            <CustomLTRow>
                                {canAssignClients && (
                                    <CheckboxWithLabel
                                        label={t('selectAll', { lng: language })}
                                        gap="0px"
                                        bold
                                        labelStyles={{
                                            width: 'fit-content',
                                            whiteSpace: 'nowrap',
                                        }}
                                        sx={{
                                            display: 'flex',
                                            gap: '0.5rem',
                                            justifyContent: 'flex-start',
                                        }}
                                        onChange={(ev) => {
                                            handleChangeHeader(
                                                (ev.target as HTMLInputElement).checked
                                            );
                                        }}
                                        checked={checkedHeader}
                                        disabled={clientsAppsForTable?.length === 0}
                                    />
                                )}
                            </CustomLTRow>
                        </S.SpanForMobile>

                        {clientsAppsForTable.map((clientApp) => (
                            <CustomLTRow
                                key={`client_${clientApp.id}`}
                                id={`client_${clientApp.id}`}
                                style={{ paddingLeft: '16px', minWidth: desktopTableMinWidth }}
                                wrapperStyle={mobile ? undefined : casesGridStyle}
                            >
                                {canAssignClients && (
                                    <LTItem
                                        tip={t('select', { lng: language })}
                                        maxWidth="20px"
                                        minWidth="20px"
                                        style={{
                                            textAlign: 'center',
                                            marginRight: '1rem',
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedApps.includes(clientApp.id)}
                                            onChange={(__, checked) =>
                                                handleSelectApp(clientApp.id, checked)
                                            }
                                        />
                                    </LTItem>
                                )}
                                {canSetPaymentStatus && (
                                    <LTItem
                                        tip={t('setPaymentStatus', { lng: language })}
                                        maxWidth="60px"
                                        minWidth="60px"
                                        style={{
                                            textAlign: 'center',
                                            marginRight: '0.4rem',
                                        }}
                                    >
                                        <PaymentStatusMenuButton
                                            status={clientApp.payment_status}
                                            dollarStyle={{ paddingTop: '2px' }}
                                            onSelect={(status) =>
                                                handlePaymentStatusChange(clientApp.id, status)
                                            }
                                        />
                                    </LTItem>
                                )}

                                <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    tip={caseIdDisplayValue(clientApp, selectedCaseIdType)}
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {caseIdDisplayValue(clientApp, selectedCaseIdType)}
                                </LTItem>
                                <LTItem
                                    maxWidth="130px"
                                    minWidth="130px"
                                    tip={`${clientApp.firstname} ${clientApp.lastname}`}
                                    style={{ marginRight: '1rem' }}
                                >
                                    <S.Link
                                        onClick={() => dispatch(loadApp(clientApp.id.toString()))}
                                        to={`${clientApp.candidate_id}/client-profile`}
                                    >
                                        {`${clientApp.firstname} ${clientApp.lastname}`}
                                    </S.Link>
                                </LTItem>
                                <LTItem
                                    maxWidth="200px"
                                    minWidth="200px"
                                    tip={clientApp.email}
                                    style={{ marginRight: '1rem' }}
                                >
                                    {clientApp.email}
                                </LTItem>
                                <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    style={{ marginRight: '1rem' }}
                                >
                                    {clientApp.program_name}
                                </LTItem>

                                <LTItem
                                    maxWidth="170px"
                                    minWidth="170px"
                                    tip={
                                        clientApp.client_status
                                            ? clientApp.client_status.toString().toLowerCase()
                                            : ''
                                    }
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    <S.ITAStatusWrapper>
                                        {clientApp.marked_awaiting_ita_at !== null && (
                                            <S.ITAStatusIndicator />
                                        )}
                                        <StatusDropdown
                                            options={caseStatusOptions}
                                            onChange={(selected: StatusOption) => {
                                                const nextValue = selected.value ?? '';
                                                if (
                                                    !nextValue ||
                                                    nextValue === clientApp.client_status
                                                ) {
                                                    return;
                                                }
                                                saveResultsOutcome(clientApp, nextValue);
                                                setSelectedStatus(nextValue);
                                            }}
                                            initialValue={
                                                clientApp.client_status
                                                    ? clientApp.client_status
                                                    : 'NOT_ASSIGNED'
                                            }
                                        />
                                    </S.ITAStatusWrapper>
                                </LTItem>

                                <LTItem
                                    maxWidth="90px"
                                    minWidth="90px"
                                    style={{ textAlign: 'center' }}
                                >
                                    {clientApp.tickets_open ?? 0}
                                </LTItem>
                                <LTItem
                                    maxWidth="90px"
                                    minWidth="90px"
                                    style={{ textAlign: 'center' }}
                                >
                                    {clientApp.tickets_resolved ?? 0}
                                </LTItem>

                                <LTItem maxWidth="140px" minWidth="140px">
                                    {clientApp.last_status_updated_at ? (
                                        (() => {
                                            const days = getDaysSince(
                                                clientApp.last_status_updated_at.toString()
                                            );
                                            return (
                                                <div
                                                    style={{
                                                        textAlign: 'center',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        marginLeft: '3rem',
                                                    }}
                                                >
                                                    {days}
                                                    {iconEligibleStatuses.has(
                                                        clientApp.client_status
                                                    ) &&
                                                        days > 7 && <ExclMark />}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <div
                                            style={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                marginLeft: '3rem',
                                            }}
                                        >
                                            --
                                        </div>
                                    )}
                                </LTItem>

                                <LTItem
                                    maxWidth="130px"
                                    minWidth="130px"
                                    tip={
                                        clientApp.backoffice_name
                                            ? clientApp.backoffice_name.toString().toLowerCase()
                                            : ''
                                    }
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {backofficeAgents?.length ? (
                                        <AgentDropdown
                                            agents={backofficeAgents}
                                            defaultAgent={clientApp.backoffice_id ?? undefined}
                                            onChange={(agentId: number) => {
                                                assignAgents(
                                                    undefined,
                                                    agentId,
                                                    undefined,
                                                    clientApp.id
                                                );
                                            }}
                                        />
                                    ) : (
                                        '--'
                                    )}
                                </LTItem>

                                <LTItem
                                    maxWidth="130px"
                                    minWidth="130px"
                                    tip={
                                        clientApp.consultant_name
                                            ? clientApp.consultant_name.toString().toLowerCase()
                                            : ''
                                    }
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {visaAgents?.length ? (
                                        <AgentDropdown
                                            agents={visaAgents}
                                            defaultAgent={clientApp.consultant_id ?? undefined}
                                            onChange={(agentId: number) => {
                                                assignAgents(
                                                    undefined,
                                                    undefined,
                                                    agentId,
                                                    clientApp.id
                                                );
                                            }}
                                        />
                                    ) : clientApp.consultant_name ? (
                                        clientApp.consultant_name
                                    ) : (
                                        '--'
                                    )}
                                </LTItem>

                                <LTItem
                                    maxWidth="130px"
                                    minWidth="130px"
                                    tip={
                                        clientApp.sales_name
                                            ? clientApp.sales_name.toString().toLowerCase()
                                            : ''
                                    }
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {salesAgents?.length ? (
                                        <AgentDropdown
                                            agents={salesAgents}
                                            defaultAgent={clientApp.sales_id ?? undefined}
                                            onChange={(agentId: number) => {
                                                assignAgents(
                                                    agentId,
                                                    undefined,
                                                    undefined,
                                                    clientApp.id
                                                );
                                            }}
                                        />
                                    ) : (
                                        '--'
                                    )}
                                </LTItem>

                                <LTItem
                                    maxWidth="160px"
                                    minWidth="160px"
                                    tip={
                                        organizations.find(
                                            (organization) =>
                                                organization.value ===
                                                clientApp.service_provider_id?.toString()
                                        )?.label ||
                                        clientApp.organization_name ||
                                        ''
                                    }
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    <OptionDropdown
                                        options={organizations}
                                        value={clientApp.service_provider_id?.toString() || ''}
                                        onChange={(organizationId) =>
                                            saveOrganization(clientApp.id, organizationId)
                                        }
                                        placeholder={clientApp.organization_name || '--'}
                                        width={160}
                                    />
                                </LTItem>
                                <LTItem
                                    maxWidth="110px"
                                    minWidth="110px"
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {clientApp.date_agreement_signed
                                        ? formatDate(clientApp.date_agreement_signed.toString())
                                        : '--'}
                                </LTItem>
                                <LTItem
                                    maxWidth="110px"
                                    minWidth="110px"
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {clientApp.date_offer_sent
                                        ? formatDate(clientApp.date_offer_sent.toString())
                                        : '--'}
                                </LTItem>
                                <LTItem
                                    maxWidth="110px"
                                    minWidth="110px"
                                    style={{ textAlign: 'center' }}
                                >
                                    {clientApp.date_fully_paid
                                        ? formatDate(clientApp.date_fully_paid.toString())
                                        : '--'}
                                </LTItem>
                                <LTItem
                                    maxWidth="70px"
                                    minWidth="70px"
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {formatCurrency(clientApp.value)}
                                </LTItem>
                                {canAssignClients && (
                                    <LTMobileItem
                                        style={{ minWidth: '132px' }}
                                        name={t('assign', { lng: language })}
                                    >
                                        <Checkbox
                                            checked={selectedApps.includes(clientApp.id)}
                                            onChange={(__, checked) =>
                                                handleSelectApp(clientApp.id, checked)
                                            }
                                        />
                                    </LTMobileItem>
                                )}
                                {canSetPaymentStatus && (
                                    <LTMobileItem
                                        name={t('paid', { lng: language })}
                                        style={{
                                            alignItems: 'flex-start',
                                            maxHeight: '20px',
                                            minWidth: '132px',
                                        }}
                                    >
                                        <PaymentStatusMenuButton
                                            status={clientApp.payment_status}
                                            dollarStyle={{
                                                paddingRight: '4px',
                                                paddingBottom: '2px',
                                            }}
                                            onSelect={(status) =>
                                                handlePaymentStatusChange(clientApp.id, status)
                                            }
                                        />
                                    </LTMobileItem>
                                )}

                                <LTMobileItem style={{ minWidth: '132px' }} name={caseIdLabel}>
                                    {caseIdDisplayValue(clientApp, selectedCaseIdType)}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('name', { lng: language })}
                                >
                                    <S.Link
                                        onClick={() => dispatch(loadApp(clientApp.id.toString()))}
                                        to={`${clientApp.candidate_id}/client-profile`}
                                    >
                                        {`${clientApp.firstname} ${clientApp.lastname}`}
                                    </S.Link>
                                </LTMobileItem>
                                <span style={{ width: '87vw' }}>
                                    <LTMobileItem
                                        style={{ minWidth: '132px' }}
                                        name={t('service', { lng: language })}
                                    >
                                        {clientApp.program_name}
                                    </LTMobileItem>
                                </span>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('daysInStatus', { lng: language })}
                                >
                                    {clientApp.last_status_updated_at
                                        ? (() => {
                                              const days = getDaysSince(
                                                  clientApp.last_status_updated_at.toString()
                                              );
                                              return (
                                                  <>
                                                      {days}
                                                      {iconEligibleStatuses.has(
                                                          clientApp.client_status
                                                      ) &&
                                                          days > 7 && <ExclMark />}
                                                  </>
                                              );
                                          })()
                                        : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('fileStatus', { lng: language })}
                                >
                                    <S.ITAStatusWrapper>
                                        {clientApp.marked_awaiting_ita_at !== null && (
                                            <S.ITAStatusIndicator />
                                        )}
                                        {getFileStatus(clientApp.client_status)}
                                    </S.ITAStatusWrapper>
                                </LTMobileItem>
                                <LTMobileItem style={{ minWidth: '132px' }} name="Tickets Open">
                                    {clientApp.tickets_open ?? 0}
                                </LTMobileItem>
                                <LTMobileItem style={{ minWidth: '132px' }} name="Tickets Resolved">
                                    {clientApp.tickets_resolved ?? 0}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('backOffice', { lng: language })}
                                >
                                    {clientApp.backoffice_name ? clientApp.backoffice_name : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('visaAgent', { lng: language })}
                                >
                                    {clientApp.consultant_name ? clientApp.consultant_name : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('salesAgent', { lng: language })}
                                >
                                    {clientApp.sales_name ? clientApp.sales_name : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('organization', { lng: language })}
                                >
                                    <OptionDropdown
                                        options={organizations}
                                        value={clientApp.service_provider_id?.toString() || ''}
                                        onChange={(organizationId) =>
                                            saveOrganization(clientApp.id, organizationId)
                                        }
                                        placeholder={clientApp.organization_name || '--'}
                                        width={160}
                                    />
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('retainer', { lng: language })}
                                >
                                    {clientApp.date_agreement_signed
                                        ? formatDate(clientApp.date_agreement_signed.toString())
                                        : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('offerSent', { lng: language })}
                                >
                                    {clientApp.date_offer_sent
                                        ? formatDate(clientApp.date_offer_sent.toString())
                                        : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('payment', { lng: language })}
                                >
                                    {clientApp.date_fully_paid
                                        ? formatDate(clientApp.date_fully_paid.toString())
                                        : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('amount', { lng: language })}
                                >
                                    {formatCurrency(clientApp.value)}
                                </LTMobileItem>
                            </CustomLTRow>
                        ))}
                    </>
                )}

                {isClientsAppsLoading &&
                    skeletonArray.map((_, idx) => (
                        <LTRow key={idx} style={{ height: '44px' }}>
                            <LTItem maxWidth="12%">
                                <Skeleton width={40} />
                            </LTItem>
                            <LTItem maxWidth="12%">
                                <Skeleton width={40} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="35%">
                                <Skeleton width={200} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="12%" lastItem>
                                <Skeleton width={79} />
                            </LTItem>
                        </LTRow>
                    ))}
            </S.ListTable>
            <ResultsAutcomeModal
                open={showResultsOutcomeModal}
                onClose={() => {
                    setShowResultsOutcomeModal(false);
                    setOutcomedAppId(undefined);
                    setPendingVisaApplication(null);
                    setSelectedStatus('');
                }}
                onConfirm={handleConfirmedResOutcome}
            />
            <ReceivedVisaModal
                open={showReceivedVisaModal}
                application={pendingVisaApplication}
                onClose={() => {
                    setShowReceivedVisaModal(false);
                    setOutcomedAppId(undefined);
                    setPendingVisaApplication(null);
                    setSelectedStatus('');
                }}
                onVisaReceived={handleVisaReceived}
            />
        </>
    );
};

export default CasesApplicationsTable;
